import { Fragment, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { usePermissions } from '../../hooks/usePermissions';
import { useSignOut } from '../../hooks/useSignOut';
import {
  CalendarDays,
  LayoutDashboard,
  Users,
  Settings,
  Menu,
  X,
  History,
  Clock,
  LogIn,
  Wrench,
  LogOut,
  ShieldCheck,
  ChevronsLeft,
  ChevronsRight,
  Truck,
  BookOpen,
  ShoppingCart,
  Boxes,
  PackageCheck,
} from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { AssistantLauncher } from './AssistantLauncher';
import clsx from 'clsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import logoImage from '../../assets/920a151e35875b2a6468c8afe0418ba4ce7adc2e.png';

// GH #130: each nav item carries the [feature, action] permission it requires.
// The side menu renders an item only when usePermissions().can(...) is true, so
// the visible menu matches the role's resolved permission set. Superadmin sees
// all (resolves to the full key set server-side). The Access Control entry gates
// on access_control.view (superadmin-only); its route is added in a later phase.
// GH #179: nav is organised into four labelled groups, ordered, with section
// headers rendered before the first item of each group (desktop + mobile both).
// Each item keeps its existing path/icon/permission — this is a regroup/reorder/
// rename only, not a route or RBAC change. GH #216: the four Materials-derived
// items now all live under STOCK AND INVENTORY, ordered Suppliers, Catalogue,
// Orders, Stock & Deliveries; RESOURCE MANAGEMENT keeps Staff & Subs + Assets &
// Resources. Renames: Work → "Work Scheduler", Admin → "Assets & Resources",
// Time → "Timesheets", Reports → "Dashboards", Stock → "Stock & Deliveries".
// Clock-in is NOT an entry here — it is a hard <a> to the Time PWA
// (/time/home) gated on can_clock_in, rendered inside the WORK group below.
const navItems: {
  path: string;
  label: string;
  icon: typeof CalendarDays;
  permission: [string, string];
  group?: string; // renders a section header before the first item of a new group
}[] = [
  // ─── WORK ───────────────────────────────────────────────────────────────
  {
    path: '/',
    label: 'Work Scheduler',
    icon: CalendarDays,
    permission: ['contracts_board', 'view'],
    group: 'Work',
  },
  { path: '/time', label: 'Timesheets', icon: Clock, permission: ['time', 'view'], group: 'Work' },
  {
    path: '/reports',
    label: 'Dashboards',
    icon: LayoutDashboard,
    permission: ['reports', 'view'],
    group: 'Work',
  },
  // Clock-in (can_clock_in gated) is injected after the WORK items in the render.
  // ─── STOCK AND INVENTORY ────────────────────────────────────────────────
  // GH #216: Suppliers + Catalogue moved here from RESOURCE MANAGEMENT. Section
  // ordered: Suppliers, Catalogue, Orders, Stock & Deliveries. Each item keeps
  // its own per-surface permission gate (suppliers/materials/purchasing/stock).
  {
    path: '/suppliers',
    label: 'Suppliers',
    icon: Truck,
    permission: ['suppliers', 'view'],
    group: 'Stock and Inventory',
  },
  {
    path: '/catalogue',
    label: 'Catalogue',
    icon: BookOpen,
    permission: ['materials', 'view'],
    group: 'Stock and Inventory',
  },
  {
    path: '/orders',
    label: 'Orders',
    icon: ShoppingCart,
    permission: ['purchasing', 'view'],
    group: 'Stock and Inventory',
  },
  {
    path: '/stock',
    label: 'Stock & Deliveries', // GH #216: renamed from "Stock"
    icon: Boxes,
    permission: ['stock', 'view'],
    group: 'Stock and Inventory',
  },
  // ─── RESOURCE MANAGEMENT ────────────────────────────────────────────────
  {
    path: '/staff',
    label: 'Staff & Subs',
    icon: Users,
    permission: ['staff', 'view'],
    group: 'Resource Management',
  },
  {
    path: '/admin',
    label: 'Assets & Resources',
    icon: Wrench,
    permission: ['admin', 'view'],
    group: 'Resource Management',
  },
  // ─── ADMIN ──────────────────────────────────────────────────────────────
  {
    path: '/settings',
    label: 'Settings',
    icon: Settings,
    permission: ['settings', 'view'],
    group: 'Admin',
  },
  {
    path: '/access-control',
    label: 'Access Control',
    icon: ShieldCheck,
    permission: ['access_control', 'view'],
    group: 'Admin',
  },
  {
    path: '/history',
    label: 'History',
    icon: History,
    permission: ['history', 'view'],
    group: 'Admin',
  },
];

// GH #179: section header labels are upper-cased in the issue (WORK / STOCK AND
// INVENTORY / RESOURCE MANAGEMENT / ADMIN). The renderer already applies CSS
// uppercase, so the canonical label is stored title-cased and displayed upper.

const pageTitles: Record<string, string> = {
  '/': 'Work Scheduler', // GH #179: renamed from "Work"
  '/reports': 'Dashboards', // GH #216: renamed from "Reports & Analytics"
  '/staff': 'Staff & Subcontractors',
  '/history': 'History', // GH #194: subtitle changed from "Historic Snapshots"
  '/time': 'Timesheets', // GH #216: renamed from "Time"
  '/suppliers': 'Suppliers',
  '/catalogue': 'Catalogue',
  '/orders': 'Orders',
  '/stock': 'Stock',
  '/admin': 'Assets & Resources', // GH #194: the /admin route is the Assets & Resources page
  '/settings': 'Integrations & Settings',
  '/access-control': 'Access Control',
};

// #97: persisted across sessions (Tom 2026-06-09) — collapsed desktop nav.
const NAV_COLLAPSED_KEY = 'cb-nav-collapsed';

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // #97: desktop nav collapse (icons-only). Persisted in localStorage so the
  // planner's choice survives reloads/sessions. Mobile nav is unaffected — it
  // already collapses behind the hamburger.
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(NAV_COLLAPSED_KEY, String(navCollapsed));
    } catch {
      /* localStorage unavailable (private mode / SSR) — non-fatal */
    }
  }, [navCollapsed]);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, signingOut, error: signOutError } = useSignOut();
  // GH #194: the top white bar shows the nav GROUP as the title and the page name
  // as the sub-title (mirrors the #179 grouped nav). Group is derived from navItems.
  const pageSubtitle = pageTitles[location.pathname] || 'Work Scheduler';
  const pageGroup = navItems.find((item) => item.path === location.pathname)?.group ?? 'Work';
  // GH #130: usePermissions() reads the same ['me'] cache and exposes can(...)
  // for per-item nav gating.
  const { can, me } = usePermissions();
  // Nav items the caller may see, derived from their resolved permission set.
  const visibleNavItems = navItems.filter((item) => can(...item.permission));
  // GH #179: Clock-in is gated on the can_clock_in capability (the Time PWA
  // access signal on /me), independent of CB role/permissions. It renders at the
  // end of the WORK group in both desktop + mobile navs.
  const showClockIn = Boolean(me?.can_clock_in);

  // 83-03 (77#2): Stock Control is a hard <a> to the Time PWA (/time/stock-control),
  // rendered next to Clock-in at the end of the WORK group. It is gated on the
  // planner+ tier (planner/admin/finance/superadmin) — which ALWAYS resolves to
  // stock-control access server-side (stockControlAuth: is_planner_plus ⇒
  // can_access), so the link is never dead and needs no extra access call. Unlike
  // Clock-in it does NOT require can_clock_in (the whole point of 77#2: a desk
  // planner without clock-in can reach Stock Control). Team-leaders-today who are
  // viewers reach it via the /time home stock card instead.
  const showStockControl = ['planner', 'admin', 'finance', 'superadmin'].includes(me?.role ?? '');

  // 14-05: role + capability-based redirect for users without CB privileges.
  //   basic_access + can_clock_in=true → Time PWA
  //   basic_access + can_clock_in=false → /no-access
  //   Higher roles → render CB normally (Layout's Clock-in side menu link
  //                   below shows for any user with can_clock_in=true).
  useEffect(() => {
    if (!me) return;
    if (me.role === 'basic_access') {
      if (me.can_clock_in) {
        window.location.assign('/time/home');
      } else {
        navigate('/no-access', { replace: true });
      }
    }
  }, [me, navigate]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar (Desktop) — #97: collapses to icons-only via the toggle near
          the bottom; state persists in localStorage. */}
      <aside
        className={clsx(
          'hidden md:flex flex-col bg-[#1a1a1a] text-neutral-400 shrink-0 transition-[width] duration-150',
          navCollapsed ? 'w-16' : 'w-60',
        )}
      >
        <div
          className={clsx(
            'flex items-center h-16 border-b border-neutral-800 overflow-hidden',
            navCollapsed ? 'px-2 justify-center' : 'px-5',
          )}
        >
          {navCollapsed ? (
            // #97: collapsed → the dedicated "R" brand mark (the app favicon,
            // a square R), not a crop of the wordmark. brightness-0 invert
            // renders the orange mark white to match the expanded wordmark on
            // the dark sidebar.
            <img
              src="/favicon-192.png"
              alt="Reprotec"
              className="w-8 h-8 object-contain brightness-0 invert shrink-0"
            />
          ) : (
            <img src={logoImage} alt="Reprotec" className="h-7 w-auto brightness-0 invert" />
          )}
        </div>

        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {visibleNavItems.map((item, i, arr) => (
            <Fragment key={item.path}>
              {item.group && item.group !== arr[i - 1]?.group && !navCollapsed && (
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                  {item.group}
                </div>
              )}
              <NavLink
                to={item.path}
                end={item.path === '/'}
                title={navCollapsed ? item.label : undefined}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center px-3 py-2 rounded-md text-[13px] transition-colors',
                    navCollapsed && 'justify-center',
                    isActive
                      ? 'bg-[#F26522]/15 text-[#F26522]'
                      : 'hover:bg-neutral-800 hover:text-white',
                  )
                }
              >
                <item.icon
                  className={clsx('w-[18px] h-[18px] shrink-0', !navCollapsed && 'mr-3')}
                />
                {!navCollapsed && item.label}
              </NavLink>
              {/*
                GH #179 / 14-05: Clock-in sits at the end of the WORK group,
                gated on can_clock_in (Time PWA capability on /me), regardless of
                CB role. Time PWA lives at /time/home — a separate Next.js
                basePath app — so use a plain <a> for hard navigation rather than
                NavLink. Rendered when this is the last WORK-group item.
              */}
              {item.group === 'Work' && arr[i + 1]?.group !== 'Work' && showClockIn && (
                <a
                  href="/time/home"
                  title={navCollapsed ? 'Clock-in' : undefined}
                  className={clsx(
                    'flex items-center px-3 py-2 rounded-md text-[13px] transition-colors hover:bg-neutral-800 hover:text-white',
                    navCollapsed && 'justify-center',
                  )}
                >
                  <LogIn className={clsx('w-[18px] h-[18px] shrink-0', !navCollapsed && 'mr-3')} />
                  {!navCollapsed && 'Clock-in'}
                </a>
              )}
              {/*
                83-03 (77#2): Stock Control sits alongside Clock-in at the end of
                the WORK group — a hard <a> to the Time PWA (/time/stock-control),
                gated on planner+ (independent of can_clock_in).
              */}
              {item.group === 'Work' && arr[i + 1]?.group !== 'Work' && showStockControl && (
                <a
                  href="/time/stock-control"
                  title={navCollapsed ? 'Stock Control' : undefined}
                  className={clsx(
                    'flex items-center px-3 py-2 rounded-md text-[13px] transition-colors hover:bg-neutral-800 hover:text-white',
                    navCollapsed && 'justify-center',
                  )}
                >
                  <PackageCheck
                    className={clsx('w-[18px] h-[18px] shrink-0', !navCollapsed && 'mr-3')}
                  />
                  {!navCollapsed && 'Stock Control'}
                </a>
              )}
            </Fragment>
          ))}
        </nav>

        {/* #97: collapse/expand toggle — "<<" when expanded, ">>" when collapsed.
            Right-aligned when expanded, centered when collapsed. */}
        <div className={clsx('px-3 pb-1 flex', navCollapsed ? 'justify-center' : 'justify-end')}>
          <button
            onClick={() => setNavCollapsed((v) => !v)}
            className="p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-neutral-800 transition-colors"
            title={navCollapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={navCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {navCollapsed ? (
              <ChevronsRight className="w-4 h-4" />
            ) : (
              <ChevronsLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        <div className={clsx('border-t border-neutral-800', navCollapsed ? 'p-2' : 'p-4')}>
          {navCollapsed ? (
            // #97: collapsed → the SU avatar itself is the Sign-out control
            // (mobile-style, Tom 2026-06-09). Name/role/separate logout button
            // are hidden; clicking the avatar opens the same confirm dialog.
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full flex justify-center"
              title={`${me?.name ?? 'User'} — Sign out`}
              aria-label="Sign out"
            >
              <div className="w-8 h-8 rounded-full bg-[#F26522] flex items-center justify-center text-xs text-white hover:ring-2 hover:ring-white/40 transition-all">
                {me?.initials ?? '??'}
              </div>
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#F26522] flex items-center justify-center text-xs text-white">
                {me?.initials ?? '??'}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-[13px] text-white truncate">{me?.name ?? '—'}</span>
                <span className="text-[11px] text-neutral-500">{me?.role ?? '—'}</span>
              </div>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-neutral-800 transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* 61-03 T7: transparency — the worker-facing notice is hosted in the
              Time PWA (/time/** Firebase rewrite); office staff reach it here. */}
          {!navCollapsed && (
            <a
              href="/time/privacy"
              className="block mt-2 text-[11px] text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              Privacy notice
            </a>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#f5f5f5]">
        {/* Top Header */}
        <header className="h-14 flex items-center justify-between px-4 sm:px-6 bg-white border-b border-neutral-200 z-20 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-md text-neutral-500 hover:bg-neutral-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden sm:block leading-tight">
              <h1 className="text-[15px] font-medium text-neutral-800">{pageGroup}</h1>
              <p className="text-[12px] text-neutral-500">{pageSubtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell
              onSelectJob={(jobId) =>
                window.dispatchEvent(new CustomEvent('select-job', { detail: { jobId } }))
              }
            />

            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden flex items-center"
              title="Open menu"
            >
              <div className="w-8 h-8 rounded-full bg-[#F26522] flex items-center justify-center text-xs text-white">
                {me?.initials ?? '??'}
              </div>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-[#1a1a1a] shadow-xl flex flex-col">
            <div className="flex items-center justify-between h-14 px-5 border-b border-neutral-800">
              <img src={logoImage} alt="Reprotec" className="h-6 w-auto brightness-0 invert" />
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 text-neutral-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 py-4 px-3 space-y-0.5 text-neutral-400">
              {visibleNavItems.map((item, i, arr) => (
                <Fragment key={item.path}>
                  {item.group && item.group !== arr[i - 1]?.group && (
                    <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                      {item.group}
                    </div>
                  )}
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center px-3 py-2.5 rounded-md text-[13px] transition-colors',
                        isActive
                          ? 'bg-[#F26522]/15 text-[#F26522]'
                          : 'hover:bg-neutral-800 hover:text-white',
                      )
                    }
                  >
                    <item.icon className="w-[18px] h-[18px] mr-3 shrink-0" />
                    {item.label}
                  </NavLink>
                  {/* GH #179: Clock-in at the end of the WORK group (mobile),
                      gated on can_clock_in. Hard <a> to the Time PWA. */}
                  {item.group === 'Work' && arr[i + 1]?.group !== 'Work' && showClockIn && (
                    <a
                      href="/time/home"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center px-3 py-2.5 rounded-md text-[13px] transition-colors hover:bg-neutral-800 hover:text-white"
                    >
                      <LogIn className="w-[18px] h-[18px] mr-3 shrink-0" />
                      Clock-in
                    </a>
                  )}
                  {/* 83-03 (77#2): Stock Control at the end of the WORK group
                      (mobile), gated on planner+. Hard <a> to the Time PWA. */}
                  {item.group === 'Work' && arr[i + 1]?.group !== 'Work' && showStockControl && (
                    <a
                      href="/time/stock-control"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center px-3 py-2.5 rounded-md text-[13px] transition-colors hover:bg-neutral-800 hover:text-white"
                    >
                      <PackageCheck className="w-[18px] h-[18px] mr-3 shrink-0" />
                      Stock Control
                    </a>
                  )}
                </Fragment>
              ))}
            </nav>

            <div className="p-4 border-t border-neutral-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#F26522] flex items-center justify-center text-xs text-white">
                  {me?.initials ?? '??'}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-[13px] text-white truncate">{me?.name ?? '—'}</span>
                  <span className="text-[11px] text-neutral-500">{me?.role ?? '—'}</span>
                </div>
                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    setShowLogoutConfirm(true);
                  }}
                  className="p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-neutral-800 transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={showLogoutConfirm}
        onOpenChange={(open) => {
          if (!signingOut) setShowLogoutConfirm(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to sign out?</AlertDialogDescription>
          </AlertDialogHeader>
          {signOutError && (
            <p role="alert" className="text-[13px] text-red-700">
              {signOutError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={signingOut}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={signingOut}
              onClick={(event) => {
                event.preventDefault();
                void signOut();
              }}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 65-03: floating assistant launcher — present on every page,
          self-gates on contracts_board.view. */}
      <AssistantLauncher />
    </div>
  );
}
